import { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Button, Loader, Alert, Dialog, Input, Checkbox } from '@dreamtree-org/twreact-ui';
import { Pencil, Trash2 } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import { getApiRoot } from '@/lib/domainResolver';
import Can from '@rbac/Can';
import { useAuth, useCan } from '@rbac/useCan';
import { fieldsFor, columnsFor, initialValues, toPayload } from './crudFields';

/**
 * Generic list + create/edit/delete page for a model, composed entirely from twreact-ui.
 *
 * Columns and form fields both come from GET /api/meta/schema/:model (korm's describeModel),
 * NOT from the first row. Deriving them from data meant a model with no rows rendered no
 * columns at all — a new install looked broken rather than empty.
 *
 * The write buttons here are convenience, not security: every request still goes through
 * /api/crud, where BaseModel.validate authorizes it and the model's own hooks can refuse.
 * `readOnly` in the metadata just avoids drawing a button whose request always 400s.
 */
export default function CrudPage({ model, module, title }) {
  const { fetchApi } = useAppApi();
  const user = useAuth();
  const can = useCan();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  // Separate from `err`: this one explains why the page has no write controls, which is a
  // different thing from a request having failed.
  const [metaErr, setMetaErr] = useState('');

  const [editing, setEditing] = useState(null); // { row } for edit, {} for create
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fields = fieldsFor(meta?.columns);

  const loadRows = useCallback(async () => {
    const res = await fetchApi('crud', { model, action: 'list', limit: 50 });
    setRows(Array.isArray(res) ? res : (res && res.data) || []);
  }, [fetchApi, model]);

  useEffect(() => {
    let on = true;
    setLoading(true);
    setErr('');
    setMetaErr('');
    setMeta(null);
    (async () => {
      try {
        // Metadata failing is not fatal: the table can still render from the rows it has,
        // it just loses its headers when empty and cannot offer a form. It IS reported,
        // though — silently swallowing it turned every write button off and left the page
        // looking like a read-only feature rather than a broken one.
        const schema = await fetch(`${getApiRoot()}/api/meta/schema/${model}`, { credentials: 'include' })
          .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`schema request failed (HTTP ${r.status})`))))
          .catch((ex) => {
            if (on) setMetaErr(ex.message);
            return null;
          });
        if (on && schema) setMeta(schema);
        await loadRows();
      } catch (ex) {
        if (on) setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => { on = false; };
  }, [model, loadRows]);

  const openCreate = () => {
    const f = fieldsFor(meta?.columns);
    setValues(initialValues(f, null));
    setEditing({});
  };

  const openEdit = useCallback((row) => {
    const f = fieldsFor(meta?.columns);
    setValues(initialValues(f, row));
    setEditing({ row });
  }, [meta]);

  const save = async () => {
    setSaving(true);
    setErr('');
    try {
      const data = toPayload(fields, values);
      if (editing.row) {
        await fetchApi('crud', { model, action: 'update', where: { id: editing.row.id }, data });
      } else {
        await fetchApi('crud', { model, action: 'create', data });
      }
      setEditing(null);
      await loadRows();
    } catch (ex) {
      // The model hooks are the real gate — a refusal from one of them lands here and must
      // read clearly (e.g. Permission's "run rbac:setup", ApiToken's "use issue/rotate").
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const row = confirmDelete;
    setConfirmDelete(null);
    setErr('');
    try {
      await fetchApi('crud', { model, action: 'delete', where: { id: row.id } });
      await loadRows();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    }
  };

  const writable = Boolean(meta) && !meta.readOnly;

  // Metadata drives the headers; fall back to the first row only when the schema call failed.
  const columns = meta?.columns?.length
    ? columnsFor(meta.columns)
    : Object.keys(rows[0] || {}).map((k) => ({ key: k, label: k }));

  // `replaceDefaultActions` is not optional: supplied actions otherwise MERGE onto the
  // library's built-in Edit/Delete/View, whose handlers are console.log stubs — the exact
  // live-looking dead affordance this app opts out of everywhere else.
  // Keyed on `user`, not `can`: useCan() returns a fresh closure every render, so listing it
  // would defeat the memo the library relies on for its per-row action cache.
  const actions = useMemo(() => [
    {
      name: 'edit',
      label: 'Edit',
      icon: <Pencil size={16} />,
      inline: true,
      hidden: () => !can(module, 'update'),
      onClick: ({ row }) => openEdit(row),
    },
    {
      name: 'delete',
      label: 'Delete',
      icon: <Trash2 size={16} />,
      inline: true,
      hidden: () => !can(module, 'delete'),
      onClick: ({ row }) => setConfirmDelete(row),
    },
  ], [user, module, openEdit]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-xl font-bold">{title || model}</h1>
        {writable ? (
          <Can module={module} action="create">
            <Button size="sm" onClick={openCreate} disabled={!fields.length}>New {model}</Button>
          </Can>
        ) : null}
      </div>

      {/* Say WHY there are no buttons, rather than looking like a missing feature. */}
      {meta?.readOnly ? (
        <Alert variant="info" title={`${model} is managed elsewhere`}>{meta.readOnlyReason}</Alert>
      ) : null}

      {metaErr ? (
        <Alert variant="warning" title="Editing is unavailable">
          This page could not load {model}&apos;s schema, so it cannot show the create or edit
          form. The rows below are still current. ({metaErr})
        </Alert>
      ) : null}

      {err ? <Alert variant="error" title="Something went wrong">{err}</Alert> : null}

      {loading ? <Loader text="Loading…" /> : (
        <Table
          data={rows}
          columns={columns}
          pagination
          globalSearch
          withAction={writable}
          actions={actions}
          replaceDefaultActions
        />
      )}

      {editing ? (
        <Dialog isOpen onClose={() => setEditing(null)} title={editing.row ? `Edit ${model} #${editing.row.id}` : `New ${model}`}>
          <div className="space-y-3">
            {fields.map((f) => (f.widget === 'checkbox' ? (
              <Checkbox
                key={f.name}
                label={f.name}
                checked={Boolean(values[f.name])}
                onChange={(e) => setValues((v) => ({ ...v, [f.name]: e?.target ? e.target.checked : Boolean(e) }))}
              />
            ) : (
              <Input
                key={f.name}
                label={f.name + (f.required ? ' *' : '')}
                type={f.widget === 'textarea' ? 'text' : f.widget}
                value={values[f.name] ?? ''}
                required={f.required}
                onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
              />
            )))}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
              <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            </div>
          </div>
        </Dialog>
      ) : null}

      {confirmDelete ? (
        <Dialog isOpen onClose={() => setConfirmDelete(null)} title={`Delete ${model} #${confirmDelete.id}?`}>
          <p className="text-sm">This cannot be undone.</p>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button onClick={doDelete}>Delete</Button>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
